import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Camera, ArrowLeft } from 'lucide-react';

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100">
      <nav className="glass fixed top-0 left-0 right-0 z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center space-x-2">
                <Camera className="w-8 h-8 text-blue-600" />
                <span className="text-2xl font-bold text-slate-800">FlockTracker</span>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-4">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8">
            <h1 className="text-4xl font-bold text-slate-800 mb-6">Privacy Policy</h1>
            <p className="text-sm text-slate-500 mb-8">Effective Date: November 6, 2025</p>

            <div className="space-y-6 text-slate-700">
              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">1. Information We Collect</h2>
                <p>When you use FlockTracker, we collect the following information:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li><strong>Account Information:</strong> Email address, full name, phone number (optional)</li>
                  <li><strong>User Content:</strong> Camera location reports, stories, comments, and uploaded images</li>
                  <li><strong>Technical Data:</strong> IP address, browser type, device information, and usage data</li>
                  <li><strong>Location Data:</strong> GPS coordinates when you report camera locations (only when you explicitly provide them)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">2. How We Use Your Information</h2>
                <p>We use your information to:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Provide and maintain the FlockTracker service</li>
                  <li>Display your contributions (camera locations, stories, comments) on the platform</li>
                  <li>Communicate with you about your account and service updates</li>
                  <li>Send newsletters if you've opted in</li>
                  <li>Moderate content and enforce our Terms of Use</li>
                  <li>Improve and develop our services</li>
                  <li>Ensure security and prevent fraud</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">3. Information Sharing</h2>
                <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li><strong>Public Content:</strong> Camera locations, stories, and comments you submit are publicly visible to all users</li>
                  <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                  <li><strong>Service Providers:</strong> With trusted third parties who help us operate the platform</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">4. Your Email Address</h2>
                <p>Your email address is used for:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Account authentication and password recovery</li>
                  <li>Displaying alongside your public contributions (camera reports, stories, comments)</li>
                  <li>Sending newsletters if you've opted in</li>
                  <li>Important service notifications</li>
                </ul>
                <p className="mt-2 font-semibold">Note: Your email will be visible to other users when you make public contributions to help establish transparency and accountability in the community.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">5. Data Security</h2>
                <p>We implement reasonable security measures to protect your information, including:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Encrypted password storage using bcrypt</li>
                  <li>Secure HTTPS connections</li>
                  <li>JWT token-based authentication</li>
                </ul>
                <p className="mt-2">However, no method of transmission over the internet is 100% secure. We cannot guarantee absolute security.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">6. Cookies and Tracking</h2>
                <p>We use local storage to maintain your login session. We do not use third-party advertising cookies or tracking pixels.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">7. Your Rights</h2>
                <p>You have the right to:</p>
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>Access your personal information</li>
                  <li>Correct inaccurate information</li>
                  <li>Request deletion of your account</li>
                  <li>Opt out of newsletters at any time</li>
                  <li>Export your data</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">8. Children's Privacy</h2>
                <p>FlockTracker is not intended for children under 13. We do not knowingly collect information from children under 13.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">9. Changes to This Policy</h2>
                <p>We may update this Privacy Policy from time to time. We will notify users of significant changes via email or platform notification.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-slate-800 mb-3">10. Contact Us</h2>
                <p>For questions about this Privacy Policy or to exercise your rights, please contact us through our website.</p>
              </section>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}